import React from 'react';
import PidDiagramShell, { PidPipe, PidPort } from './PidDiagramShell';
import { PulpPaperRoute } from '../../types';

/** 废纸脱墨浆（DIP）P&ID 详细图：碎浆 → 除筛 → 两段浮选 → 洗涤 → 漂白，油墨污泥外排 */
const media = {
  pulp: { label: '废纸 / 纸浆', light: '#ef6c00', dark: '#ffb74d', width: 2.6 },
  chem: { label: '脱墨剂 / 油墨污泥', light: '#00838f', dark: '#4dd0e1', width: 2 },
  water: { label: '水', light: '#1565c0', dark: '#64b5f6', width: 2 },
  steam: { label: '蒸汽', light: '#d32f2f', dark: '#e57373', width: 2 },
};

const devices = [
  { id: 'd101', kind: 'digester' as const, x: 100, y: 115, tag: 'D-101', name: '碎浆机', note: '10~12% 55℃' },
  { id: 'w101', kind: 'washer' as const, x: 290, y: 188, tag: 'W-101', name: '高浓除筛' },
  { id: 'fl101', kind: 'clarifier' as const, x: 460, y: 182, tag: 'FL-101', name: '浮选槽一' },
  { id: 'fl102', kind: 'clarifier' as const, x: 630, y: 182, tag: 'FL-102', name: '浮选槽二' },
  { id: 'w102', kind: 'washer' as const, x: 800, y: 188, tag: 'W-102', name: '洗涤浓缩' },
  { id: 'b101', kind: 'tower' as const, x: 960, y: 125, tag: 'B-101', name: '漂白塔', note: 'H₂O₂' },
];

const ports: PidPort[] = [
  { id: 'p-waste', label: '废纸', medium: 'pulp', x: 0, y: 224, direction: 'in' as const },
  { id: 'p-pulp', label: '脱墨浆', medium: 'pulp', x: 1130, y: 224, direction: 'out' as const },
  { id: 'p-chem', label: '脱墨剂', medium: 'chem', x: 90, y: 40, direction: 'in' as const },
  { id: 'p-chem2', label: 'H₂O₂', medium: 'chem', x: 920, y: 40, direction: 'in' as const },
  { id: 'p-water', label: '洗涤水', medium: 'water', x: 760, y: 120, direction: 'in' as const },
  { id: 'p-sludge', label: '油墨污泥', medium: 'chem', x: 400, y: 611, direction: 'out' as const },
];

const pipes: PidPipe[] = [
  { id: 'k01', from: 'p-waste', fromSide: 'right', to: 'd101', toSide: 'left', medium: 'pulp' },
  { id: 'k02', from: 'd101', fromSide: 'right', to: 'w101', toSide: 'left', medium: 'pulp' },
  { id: 'k03', from: 'w101', fromSide: 'right', to: 'fl101', toSide: 'left', medium: 'pulp' },
  { id: 'k04', from: 'fl101', fromSide: 'right', to: 'fl102', toSide: 'left', medium: 'pulp' },
  { id: 'k05', from: 'fl102', fromSide: 'right', to: 'w102', toSide: 'left', medium: 'pulp' },
  { id: 'k06', from: 'w102', fromSide: 'right', to: 'b101', toSide: 'left', medium: 'pulp' },
  { id: 'k07', from: 'b101', fromSide: 'right', to: 'p-pulp', toSide: 'left', medium: 'pulp' },
  // 油墨污泥：浮选槽底 → 外排（重组分走下面）
  { id: 'k08', from: 'fl101', fromSide: 'bottom', to: 'p-sludge', toSide: 'left', medium: 'chem', label: '油墨污泥' },
  { id: 'k09', from: 'p-chem', fromSide: 'right', to: 'd101', toSide: 'top', medium: 'chem' },
  { id: 'k10', from: 'p-chem2', fromSide: 'right', to: 'b101', toSide: 'top', medium: 'chem' },
  { id: 'k11', from: 'p-water', fromSide: 'right', to: 'w102', toSide: 'top', medium: 'water' },
];

const DeinkedPulpDiagram: React.FC<{ route: PulpPaperRoute }> = ({ route }) => (
  <PidDiagramShell
    routeName={route.name}
    title="废纸脱墨浆（DIP）装置详细流程"
    subtitle="高浓碎浆（10~12% 55℃）→ 除筛 → 两段浮选脱墨 → 洗涤浓缩 → H₂O₂ 漂白，得率 80~90%，油墨污泥从浮选槽底外排（设备形状按 P&ID 惯例，管线按介质分色）"
    media={media}
    devices={devices}
    ports={ports}
    pipes={pipes}
    feedstock={route.feedstock}
    products={[...route.products, ...(route.byproducts ?? [])]}
  />
);

export default DeinkedPulpDiagram;
